import React, { useEffect, useState } from 'react';
import { useParams } from "react-router-dom";
import { useDispatch, useSelector, useStore } from 'react-redux';
import moment from 'moment';
import QRCode from "qrcode.react";
import { isEmpty } from 'lodash';

import { API_URLS } from '../constants/apiUrls';
import { post } from '../utils/baseFetch';
import { setTicket } from '../reducers/ticketSlice';
import { subscribeMqttTopic, unsubscribeMqttTopic, setLang } from '../reducers/systemSlice';
import { setTagSequenceList, setCounterTagList } from '../reducers/queueSlice';
import { tableColor, getStringByTheme, showElementByTheme } from '../utils/utils';
import { STATUS, ELEMENT, LANG, PATH_LOCATION } from '../constants/constants';

import { GuluLogo } from '../components/GuluLogo';
import { SiteInfo } from '../components/SiteInfo';
import { DropDown } from '../components/DropDown';
import { TagSequence } from '../components/TagSequence';

import { ReactComponent as PeopleIcon } from '../images/people.svg';
import { ReactComponent as ArrowIcon } from '../images/arrow.svg';

import '../styles/ticket.scss';

const Ticket = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const store = useStore();

  // const history = useHistory();
  const ticket = useSelector((state) => state.ticket.ticket);
  const tagSequenceList = useSelector((state) => state.queue.tagSequenceList);
  const token = useSelector((state) => state.system.token);
  const lang = useSelector((state) => state.system.lang);
  const theme = useSelector((state) => state.system.theme);
  const counterTagList = useSelector((state) => state.queue.counterTagList);

  const [color, setColor] = useState('A');

  const [displayList, setDisplayList] = useState([])

  useEffect(() => {
    if (params.language) {
      switch (params.language.toUpperCase()) {
        case LANG.EN:
        case LANG.SC:
          dispatch(setLang(params.language.toUpperCase()));
          break;
        default:
          dispatch(setLang(LANG.TC));
      }
    }

    return function unsubscribeMqtt() {
      if (ticket && ticket.siteInfo && ticket.siteInfo.siteId) {
        const mqttTopic = 'THEGULU/' + ticket.siteInfo.siteId + '/BATCH/#';
        dispatch(unsubscribeMqttTopic(mqttTopic));
      }
    };
  }, []);

  useEffect(() => {
    document.title = getStringByTheme('ticketTitle', lang, theme);
  }, [lang, theme]);

  useEffect(() => {
    const newParams = { ticketId: params.ticketId };
    post(API_URLS.TICKET, newParams, (payload) => {
      dispatch(setTicket(payload));
      if (payload.siteInfo && payload.siteInfo.siteId) {
        if (payload.tagSequence) {
          const tempTagSequence = [];
          tempTagSequence.push(payload.tagSequence);
          dispatch(setTagSequenceList({
            siteId: payload.siteInfo.siteId,
            tagSequenceList: tempTagSequence
          }));
          dispatch(setCounterTagList({
            counterTagList: (payload.counterTagList) ? payload.counterTagList : []
          }));
        }
        const mqttTopic = 'THEGULU/' + payload.siteInfo.siteId + '/BATCH/#';
        dispatch(subscribeMqttTopic(mqttTopic));
      }
    }, undefined, store);
  }, [params.ticketId, dispatch, store, lang]);

  useEffect(() => {
    if (ticket && ticket.tableType) {
      setColor(tableColor(ticket.tableType));
    }
  }, [ticket])

  useEffect(() => {
    if (ticket) {
      const tempDisplayList = [];
      // if (ticket.size && ticket.sizeSelectable) {
      //   displayList.push(<div key="tableSize">
      //     <div className="ticket-title">{getStringByTheme('tableSize', lang, theme)}</div>
      //     <div className="ticket-text">{ticket.size}</div>
      //     <div className="middle-line"></div>
      //   </div>)
      // }
      // if (ticket.tableTypeLabel) {
      //   tempDisplayList.push(<div key="tableType">
      //     <div className="ticket-title">{getStringByTheme('tableType', lang, theme)}</div>
      //     <div className="ticket-text">{ticket.tableTypeLabel}</div>
      //     <div className="middle-line"></div>
      //   </div>)
      // }
      if (ticket.startTimestamp) {
        tempDisplayList.push(<div key="date">
          <div className="ticket-title">{getStringByTheme('date', lang, theme)}</div>
          <div className="ticket-text">{moment(ticket.startTimestamp).format("YYYY-MM-DD")}</div>
          <div className="middle-line"></div>
        </div>)
      }
      if (ticket.timeSectionLabel) {
        tempDisplayList.push(<div key="timeSection">
          <div className="ticket-title">{getStringByTheme('timeSection', lang, theme)}</div>
          <div className="ticket-text">{ticket.timeSectionLabel}</div>
          <div className="middle-line"></div>
        </div>)
      }
      if (ticket.ticketType && ticket.ticketTypeLabel) {
        tempDisplayList.push(<div key="ticketType">
          <div className="ticket-title">{getStringByTheme('ticketType', lang, theme)}</div>
          <div className="ticket-text">{ticket.ticketTypeLabel}</div>
          <div className="middle-line"></div>
        </div>)
      }
      if (ticket.tags && ticket.tags.length > 0) {
        tempDisplayList.push(<div key="tags">
          <div className="ticket-title">{getStringByTheme('tags', lang, theme)}</div>
          <div className="ticket-text">
            <div>{ticket.tags.length > 0 ? ticket.tags.forEach((item) => { tempDisplayList.push(<div>{item}</div>) }) : getStringByTheme('noTag', lang, theme)}</div>
          </div>
          <div className="middle-line"></div>
        </div>)
      }
      setDisplayList(tempDisplayList);
    }

  }, [lang, ticket, theme]);

  return (
    <div className="ticket-wrapper queue-wrapper">
      {
        ticket &&
        <>
          <GuluLogo show={!token} showLang />
          {
            showElementByTheme(ELEMENT.TICKET_DETAIL_COUNTER_TAG_LIST, theme) &&
            <>
              <div className="title">{getStringByTheme('currentTagSequence', lang, theme)}</div>
              {
                counterTagList && counterTagList.length > 0 &&
                <div className="counter-tag-list">
                  {
                    counterTagList.map((counterTag, counterTagKey) => {
                      return (
                        <TagSequence key={counterTagKey} tagSequence={counterTag} />
                      )
                    })
                  }
                </div>
              }
            </>
          }
          {
            showElementByTheme(ELEMENT.TICKET_DETAIL_TAG_SEQUENCE_LIST, theme) &&
            tagSequenceList && tagSequenceList.length > 0 &&
            <div className="tag-sequence-list">
              {
                tagSequenceList.map((tagSequence, tagSequenceKey) => {
                  return (
                    <TagSequence key={tagSequenceKey} tagSequence={tagSequence} showTicketLabel={false} />
                  )
                })
              }
            </div>
          }
          <div className="ticket-box">
            <div className={`zig-zag zig-zag-${color}`}></div>
            <div className={`ticket-box-content text-${color} background-dark-${color}`}>
              <div className={`ticket-content background-dark-${color}`}>
                <SiteInfo site={ticket && ticket.siteInfo} location={PATH_LOCATION.TICKET}/>
                <div className={`ticket-number background-${color}`}>
                  {ticket.ticketLabel}
                  {
                    ticket.size && ticket.sizeSelectable &&
                    <div className="table-size">
                      <PeopleIcon className="image" />
                      <div>{ticket.size}</div>
                    </div>
                  }
                  {
                    !isEmpty(ticket.counterName) &&
                    <div className="counter-name-wrapper">
                      <ArrowIcon className="counter-arrow" />
                      <div className={`counter-name text-${color} ${(ticket.counterName.length > 1)? 'long': ''}`}>{ticket.counterName.toUpperCase()}</div>
                    </div>
                  }
                </div>
                {
                  ticket.tableTypeLabel && <div className="table-type-label">{ticket.tableTypeLabel}</div>
                }
                <div className="qrcode-wrapper">
                  {ticket.status === STATUS.ACTIVE &&
                    <div className="redeemed">{getStringByTheme('redeemed', lang, theme)}</div>
                  }
                  <QRCode id="qrCode" size={140} value={ticket.qrcode} level={'L'} renderAs={"svg"} />
                </div>
                <div className="ticket-detail-wrapper">
                  <div className="middle-line"></div>
                  <div className="ticket-detail">
                    {displayList}
                  </div>
                  <DropDown
                    titleClass="ticket-title"
                    title={getStringByTheme('tnc', lang, theme)}
                    content={ticket.termsAndConditions}
                    location={PATH_LOCATION.TICKET}
                  />
                </div>
              </div>
            </div>
            <div className={`zig-zag zig-zag-bottom zig-zag-${color}`}></div>
          </div>
        </>
      }
    </div>
  );
}

export default Ticket;